import { motion } from "framer-motion";
import { Recycle, ShieldCheck, Leaf, Building } from "lucide-react";

const EASE = [0.16, 1, 0.3, 1];

const HIGHLIGHTS = [
  { icon: Recycle, title: "Economia circular", desc: "Cada carrinho resgatado volta ao ciclo produtivo em vez de virar sucata." },
  { icon: ShieldCheck, title: "Conformidade legal", desc: "Operação dentro das normas, com respeito às pessoas e à legislação ambiental." },
  { icon: Leaf, title: "Menos poluição urbana", desc: "Carrinhos fora de córregos, praças e calçadas — cidades mais limpas." },
  { icon: Building, title: "Patrimônio preservado", desc: "Revitalização que estende a vida útil do ativo e evita descarte prematuro." },
];

export const Sustainability = () => (
  <section id="sustentabilidade" className="relative overflow-hidden py-24 sm:py-32" data-testid="sustainability-section">
    <div className="pointer-events-none absolute right-[-10%] top-10 h-[26rem] w-[26rem] rounded-full bg-[radial-gradient(circle,rgba(16,185,129,0.12)_0%,rgba(30,58,138,0)_70%)]" />
    <div className="mx-auto max-w-7xl px-5 lg:px-8">
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true, margin: "-80px" }}
        transition={{ duration: 0.8, ease: EASE }}
        className="mb-16 max-w-3xl"
      >
        <p className="font-tech text-xs font-bold uppercase tracking-[0.25em] text-[#10B981]">Meio ambiente & sustentabilidade</p>
        <h2 className="font-display mt-4 text-3xl font-extrabold tracking-tight sm:text-4xl lg:text-5xl">
          Resgatar carrinhos também é <span className="text-[#10B981]">resgatar a cidade.</span>
        </h2>
        <p className="mt-5 text-base leading-relaxed text-slate-400 sm:text-lg">
          Ao devolver os carrinhos aos supermercados, reduzimos o custo operacional, evitamos a poluição urbana e
          preservamos o meio ambiente.
        </p>
      </motion.div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-12">
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, ease: EASE }}
          className="relative overflow-hidden rounded-2xl border border-white/10 lg:col-span-7"
        >
          <img
            src="/assets/canal.jpg"
            alt="Carrinhos abandonados às margens de canal urbano"
            loading="lazy"
            className="h-80 w-full object-cover sm:h-[26rem]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A8A]/95 via-[#1E3A8A]/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-7">
            <p className="font-tech text-[10px] uppercase tracking-[0.22em] text-[#EF4444]">O problema</p>
            <p className="font-display mt-2 max-w-md text-xl font-bold leading-snug">
              Carrinhos abandonados viram lixo urbano e poluem córregos e vias públicas.
            </p>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-60px" }}
          transition={{ duration: 0.9, delay: 0.12, ease: EASE }}
          className="relative overflow-hidden rounded-2xl border border-[#10B981]/25 lg:col-span-5"
        >
          <img
            src="/assets/carts-row.jpg"
            alt="Carrinhos recuperados e organizados pela ResgateCar"
            loading="lazy"
            className="h-80 w-full object-cover sm:h-[26rem]"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-[#1E3A8A]/95 via-[#1E3A8A]/20 to-transparent" />
          <div className="absolute bottom-0 left-0 right-0 p-7">
            <p className="font-tech text-[10px] uppercase tracking-[0.22em] text-[#10B981]">A solução</p>
            <p className="font-display mt-2 text-xl font-bold leading-snug">
              Frota recuperada, revitalizada e de volta à operação.
            </p>
          </div>
        </motion.div>
      </div>

      <div className="mt-6 grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4">
        {HIGHLIGHTS.map((h, i) => (
          <motion.div
            key={h.title}
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.7, delay: i * 0.08, ease: EASE }}
            className="rounded-2xl border border-white/10 bg-[#1B3579] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-[#10B981]/40"
            data-testid={`sustainability-card-${i}`}
          >
            <h.icon className="h-6 w-6 text-[#10B981]" />
            <h3 className="font-display mt-4 text-base font-bold tracking-tight">{h.title}</h3>
            <p className="mt-2 text-sm leading-relaxed text-slate-400">{h.desc}</p>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

export default Sustainability;
